import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const TripFormDialog = ({ open, onOpenChange, car, cars, onSaved }) => {
  const { t } = useLang();
  const [carsList, setCarsList] = useState(cars || []);
  const [selectedCarId, setSelectedCarId] = useState(car?.car_id || "");
  const [returnKm, setReturnKm] = useState("");
  const [renterName, setRenterName] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && !cars) {
      api.get("/cars").then(({ data }) => setCarsList(data));
    }
    if (open) {
      setSelectedCarId(car?.car_id || "");
      setReturnKm("");
      setRenterName("");
      setNotes("");
    }
  }, [open, car, cars]);

  const currentCar = (cars || carsList).find((c) => c.car_id === selectedCarId);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedCarId) return;
    setSaving(true);
    try {
      await api.post("/trips", {
        car_id: selectedCarId,
        return_km: Number(returnKm),
        renter_name: renterName || null,
        notes: notes || null,
      });
      toast.success(t("createdTrip"));
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("invalidReturnKm"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("logReturn")}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs uppercase tracking-[0.2em]">
            {t("returnKm")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!car && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("selectCar")}</Label>
              <Select value={selectedCarId} onValueChange={setSelectedCarId}>
                <SelectTrigger data-testid="trip-form-car-select" className="bg-[#0A0A0A] border-white/10">
                  <SelectValue placeholder={t("selectCar")} />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border border-white/10 text-white">
                  {(cars || carsList).map((c) => (
                    <SelectItem key={c.car_id} value={c.car_id} data-testid={`trip-form-car-option-${c.car_id}`}>
                      {c.plate_number} · {c.make} {c.model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {currentCar && (
            <div className="text-xs text-zinc-500 uppercase tracking-[0.2em]">
              {t("currentKm")}: {(currentCar.current_km || 0).toLocaleString()} {t("km")}
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("returnKm")} *</Label>
            <Input
              data-testid="trip-form-return-km"
              type="number"
              value={returnKm}
              onChange={(e) => setReturnKm(e.target.value)}
              required
              className="bg-[#0A0A0A] border-white/10 text-lg font-semibold"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("renterName")}</Label>
            <Input
              data-testid="trip-form-renter"
              value={renterName}
              onChange={(e) => setRenterName(e.target.value)}
              className="bg-[#0A0A0A] border-white/10"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("notes")}</Label>
            <Textarea
              data-testid="trip-form-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-[#0A0A0A] border-white/10"
            />
          </div>

          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              data-testid="trip-form-cancel"
              className="border border-white/10 bg-transparent hover:bg-[#1C1C1C] px-4 py-2 text-sm"
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              disabled={saving || !selectedCarId}
              data-testid="trip-form-save"
              className="bg-white text-black px-4 py-2 text-sm font-semibold hover:bg-zinc-100 disabled:opacity-60"
            >
              {t("save")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
